import { useState } from "react";
import { DownloadOutlined, FileExcelOutlined } from "@ant-design/icons";
import { message as antdMessage } from "antd";

import { downloadNeoDocument, hasMarkdownTable } from "./messageExport";
import { splitDownloadableDocuments } from "./downloadableDocuments";
import MarkdownMessage from "./MarkdownMessage";
import styles from "./styles.module.scss";

const DownloadableDocument = ({
  markdown,
  closed,
  streaming,
}: {
  markdown: string;
  closed: boolean;
  streaming: boolean;
}) => {
  const [downloading, setDownloading] = useState<"pdf" | "xlsx" | null>(null);
  const download = async (format: "pdf" | "xlsx") => {
    if (!closed || downloading) return;
    setDownloading(format);
    try {
      await downloadNeoDocument({ markdown }, format);
    } catch (error) {
      antdMessage.error(
        `Could not create ${format === "pdf" ? "PDF" : "Excel"}: ${
          error instanceof Error ? error.message : "Unknown error"
        }`
      );
    } finally {
      setDownloading(null);
    }
  };

  return (
    <section className={styles.downloadableDocument}>
      <MarkdownMessage source={markdown} streaming={streaming && !closed} />
      <div className={styles.downloadableDocumentActions}>
        <button
          type="button"
          disabled={!closed || downloading != null}
          onClick={() => void download("pdf")}
        >
          <DownloadOutlined />
          {downloading === "pdf" ? "Preparing…" : "Download PDF"}
        </button>
        {hasMarkdownTable(markdown) && (
          <button
            type="button"
            disabled={!closed || downloading != null}
            onClick={() => void download("xlsx")}
          >
            <FileExcelOutlined />
            {downloading === "xlsx" ? "Preparing…" : "Download Excel"}
          </button>
        )}
        {!closed && <span>Finishing document…</span>}
      </div>
    </section>
  );
};

const DownloadableDocumentMessage = ({
  source,
  streaming = false,
}: {
  source: string;
  /** This is the live reply: fade newly-streamed words in. */
  streaming?: boolean;
}) => {
  const parts = splitDownloadableDocuments(source);
  if (!parts.some((part) => part.kind === "document")) {
    return <MarkdownMessage source={source} streaming={streaming} />;
  }
  return (
    <>
      {parts.map((part) =>
        part.kind === "text" ? (
          part.value.trim() ? (
            <MarkdownMessage
              key={part.index}
              source={part.value}
              streaming={streaming}
            />
          ) : null
        ) : (
          <DownloadableDocument
            key={part.index}
            markdown={part.value}
            closed={part.closed}
            streaming={streaming}
          />
        )
      )}
    </>
  );
};

export default DownloadableDocumentMessage;
